// client/src/pages/secure/PasswordFreePage.js — "Password-free Sign-in" (owner, 24 Oct 2026).
//
// Each login: trust the device in hand on the office Tailscale network (one-tap sign-in
// from then on), and add / rename / remove passkeys (fingerprint, face or device PIN).
// The managing broker also sees the site switches, device requests waiting for approval,
// and every trusted device and passkey in the office. Server: routes/passwordless.js.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { fetchTailnetTicket, passkeysSupported, registerPasskey, guessDeviceName } from '../../utils/passwordless';

const btn = 'text-sm px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:opacity-50';
const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50';
const link = 'text-sm text-blue-600 hover:underline disabled:opacity-50';
const card = 'bg-white border border-slate-200 rounded-lg p-5 space-y-3';
const input = 'border border-slate-300 rounded-md px-3 py-2 text-sm w-full sm:w-64';
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US') : 'never');
const STATUS = {
    active: ['trusted', 'bg-green-100 text-green-700'],
    pending: ['waiting for approval', 'bg-amber-100 text-amber-800'],
    denied: ['turned down', 'bg-slate-100 text-slate-500'],
    revoked: ['removed', 'bg-slate-100 text-slate-500'],
};
const Pill = ({ status }) => {
    const [text, tone] = STATUS[status] || [status, 'bg-slate-100 text-slate-600'];
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${tone}`}>{text}</span>;
};

/** One device or passkey as a small card row (reads well on a phone). */
const Row = ({ title, lines, actions }) => (
    <li className="border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-800 break-words">{title}</div>
            {lines.filter(Boolean).map((l, i) => <div key={i} className="text-xs text-slate-500 break-words">{l}</div>)}
        </div>
        <div className="flex flex-wrap gap-3">{actions}</div>
    </li>
);

function OfficePanel({ api, onChanged }) {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const load = useCallback(async () => {
        try { setS(await api.get('/api/auth/passwordless/admin')); } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const act = async (fn) => {
        setBusy(true); setMsg(''); setErr('');
        try { const r = await fn(); if (r && r.message) setMsg(r.message); await load(); if (onChanged) onChanged(); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    if (!s) return err ? <div className={card}><p className="text-sm text-red-700">{err}</p></div> : null;
    const sw = s.switches || {};
    const removeDevice = (d) => {
        if (!window.confirm(`Remove "${d.name}" for ${d.person || d.username}?\n\nIt can no longer sign in without a password.`)) return;
        const signOut = window.confirm('Also sign this person out on every device right now?\n\nChoose OK for a lost or stolen device.');
        act(() => api.delete(`/api/auth/tailnet/devices/${d.device_id}${signOut ? '?signOut=1' : ''}`));
    };
    const removePasskey = (c) => {
        if (!window.confirm(`Remove the passkey "${c.name}" of ${c.person || c.username}?`)) return;
        act(() => api.delete(`/api/auth/passkeys/${c.passkey_id}`));
    };
    return (
        <div className={card}>
            <h2 className="text-lg font-semibold text-slate-800">Office settings (managing broker)</h2>
            <p className="text-sm text-slate-600">These switches are for this site only (Illinois and Florida each have their own).</p>
            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <span className={`text-sm font-medium ${sw.tailnet ? 'text-green-700' : 'text-slate-500'}`}>
                        One-tap sign-in on Tailscale devices is {sw.tailnet ? 'ON' : 'OFF'}
                    </span>
                    <button className={btn} disabled={busy} onClick={() => act(() => api.put('/api/auth/passwordless/settings', { tailnet: !sw.tailnet }))}>
                        {sw.tailnet ? 'Switch off' : 'Switch on'}
                    </button>
                </div>
                {!s.server.tailnetConfigured && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                        The server half is not set up yet (the Tailscale address and key for this site), so nobody can trust a
                        device or see the button until the administrator finishes it.
                    </p>
                )}
                <div className="flex flex-wrap items-center gap-3">
                    <span className={`text-sm font-medium ${sw.passkeys ? 'text-green-700' : 'text-slate-500'}`}>
                        Passkeys are {sw.passkeys ? 'ON' : 'OFF'}
                    </span>
                    <button className={btn} disabled={busy} onClick={() => act(() => api.put('/api/auth/passwordless/settings', { passkeys: !sw.passkeys }))}>
                        {sw.passkeys ? 'Switch off' : 'Switch on'}
                    </button>
                </div>
                <p className="text-xs text-slate-500">Passkeys work on this address: {s.server.passkeyDomain}</p>
            </div>
            {msg && <p className="text-sm text-green-700">{msg}</p>}
            {err && <p className="text-sm text-red-700">{err}</p>}

            <h3 className="font-semibold text-slate-800 pt-2">Devices waiting for your approval</h3>
            {s.pending.length === 0 ? <p className="text-sm text-slate-500">None.</p> : (
                <ul className="space-y-2">{s.pending.map((d) => (
                    <Row key={d.device_id} title={`${d.person || d.username} — ${d.name}`}
                        lines={[`Tailscale address ${d.tailnet_ip}${d.node_os ? ` · ${d.node_os}` : ''}${d.tailnet_login ? ` · Tailscale account ${d.tailnet_login}` : ''}`,
                            `Asked ${fmt(d.requested_at)}`]}
                        actions={<>
                            <button className={btn} disabled={busy} onClick={() => act(() => api.post(`/api/auth/tailnet/devices/${d.device_id}/approve`, {}))}>Approve</button>
                            <button className={ghost} disabled={busy} onClick={() => act(() => api.post(`/api/auth/tailnet/devices/${d.device_id}/deny`, {}))}>Turn down</button>
                        </>} />
                ))}</ul>
            )}

            <h3 className="font-semibold text-slate-800 pt-2">All trusted devices in the office</h3>
            {s.devices.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
                <ul className="space-y-2">{s.devices.map((d) => (
                    <Row key={d.device_id} title={`${d.person || d.username} — ${d.name}`}
                        lines={[`Tailscale address ${d.tailnet_ip}`, `Last used ${fmt(d.last_used_at)}${d.last_used_ip ? ` from ${d.last_used_ip}` : ''}`]}
                        actions={<button className={link} disabled={busy} onClick={() => removeDevice(d)}>Remove</button>} />
                ))}</ul>
            )}

            <h3 className="font-semibold text-slate-800 pt-2">All passkeys in the office</h3>
            {s.passkeys.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
                <ul className="space-y-2">{s.passkeys.map((c) => (
                    <Row key={c.passkey_id} title={`${c.person || c.username} — ${c.name || 'Passkey'}`}
                        lines={[`Added ${fmt(c.created_at)}`, `Last used ${fmt(c.last_used_at)}${c.last_used_ip ? ` from ${c.last_used_ip}` : ''}`]}
                        actions={<button className={link} disabled={busy} onClick={() => removePasskey(c)}>Remove</button>} />
                ))}</ul>
            )}
        </div>
    );
}

export default function PasswordFreePage({ user }) {
    const api = useApiClient();
    const [me, setMe] = useState(null);
    const [here, setHere] = useState(undefined);      // ticket answer for this device; null = not on the tailnet
    const [checking, setChecking] = useState(false);
    const [deviceLabel, setDeviceLabel] = useState('');
    const [passkeyName, setPasskeyName] = useState(guessDeviceName());
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        try { setMe(await api.get('/api/auth/passwordless/me')); } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const baseUrl = me && me.config && me.config.tailnet.available ? me.config.tailnet.baseUrl : null;
    const check = useCallback(async () => {
        if (!baseUrl) return;
        setChecking(true);
        setHere(await fetchTailnetTicket(baseUrl, 4000));
        setChecking(false);
    }, [baseUrl]);
    useEffect(() => { check(); }, [check]);

    const run = async (fn) => {
        setBusy(true); setMsg(''); setErr('');
        try { const r = await fn(); if (r && r.message) setMsg(r.message); await load(); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };

    const trustThisDevice = () => run(async () => {
        const t = await fetchTailnetTicket(baseUrl, 5000);
        if (!t) throw new Error('This device cannot reach the office Tailscale network right now. Switch Tailscale on and try again.');
        const r = await api.post('/api/auth/tailnet/devices', { ticket: t.ticket, label: deviceLabel || undefined });
        setDeviceLabel('');
        await check();
        return r;
    });
    const renameDevice = (d) => {
        const label = window.prompt('A name for this device (e.g. "Office PC", "My phone"):', d.name);
        if (label != null) run(() => api.put(`/api/auth/tailnet/devices/${d.device_id}`, { label }));
    };
    const removeDevice = (d) => {
        if (!window.confirm(`Remove "${d.name}"?\n\nIt will need your password again.`)) return;
        const signOut = window.confirm('Also sign you out on every device right now?\n\nChoose OK if the device was lost or stolen (you will need to sign in again here too).');
        run(() => api.delete(`/api/auth/tailnet/devices/${d.device_id}${signOut ? '?signOut=1' : ''}`));
    };
    const addPasskey = () => run(async () => {
        const r = await registerPasskey(passkeyName || guessDeviceName());
        return r || { message: 'Cancelled — no passkey was added.' };
    });
    const renamePasskey = (c) => {
        const name = window.prompt('A name for this passkey:', c.name || '');
        if (name != null) run(() => api.put(`/api/auth/passkeys/${c.passkey_id}`, { name }));
    };
    const removePasskey = (c) => {
        if (!window.confirm(`Remove the passkey "${c.name}"?\n\nIt will no longer sign you in here.`)) return;
        run(() => api.delete(`/api/auth/passkeys/${c.passkey_id}`));
    };

    if (!me) return <div className="p-6 text-sm text-slate-600">{err || 'Loading…'}</div>;
    const mine = me.devices || [];
    const thisDevice = here ? mine.find((d) => d.tailnet_ip === here.peer) : null;
    const passkeysOn = me.config.passkeys.enabled;

    return (
        <div className="p-4 sm:p-6 max-w-3xl space-y-5">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Password-free Sign-in</h1>
                <p className="text-sm text-slate-600 mt-1">
                    Sign in without typing your password: with one tap on your own devices on the office Tailscale network, or
                    with a passkey (your fingerprint, face or device PIN) anywhere — including the BREC app. Your password still
                    works as before.
                </p>
            </div>
            {msg && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">{msg}</p>}
            {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</p>}

            {/* ---------------- Tailscale ---------------- */}
            <div className={card}>
                <h2 className="text-lg font-semibold text-slate-800">This device on Tailscale</h2>
                {!me.config.tailnet.available ? (
                    <p className="text-sm text-slate-600">Tailscale sign-in is not set up on this site yet.</p>
                ) : checking && here === undefined ? (
                    <p className="text-sm text-slate-600">Checking whether this device is on the office Tailscale network…</p>
                ) : !here ? (
                    <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                            This device is not on the office Tailscale network right now (or Tailscale is switched off on it).
                            Switch Tailscale on, then check again. The browser may ask to allow access to devices on your network — choose Allow.
                        </p>
                        <button className={ghost} disabled={checking} onClick={check}>{checking ? 'Checking…' : 'Check again'}</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-700">
                            This device is on the office Tailscale network as <strong>{here.device.name}</strong>
                            <span className="text-slate-500"> ({here.peer})</span>.
                        </p>
                        {thisDevice && thisDevice.status === 'active' ? (
                            <p className="text-sm text-green-700 font-medium">Trusted for you. On the login page, tap "Sign in as {user && user.username}".</p>
                        ) : thisDevice && thisDevice.status === 'pending' ? (
                            <p className="text-sm text-amber-800">Waiting for the managing broker to approve this device.</p>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                <input className={input} placeholder={`Name (optional), e.g. ${here.device.name}`} value={deviceLabel}
                                    maxLength={80} onChange={(e) => setDeviceLabel(e.target.value)} />
                                <button className={btn} disabled={busy} onClick={trustThisDevice}>Trust this device</button>
                            </div>
                        )}
                        {!me.canTrustAtOnce && !(thisDevice && thisDevice.status === 'active') && (
                            <p className="text-xs text-slate-500">The managing broker approves each device before it can sign you in.</p>
                        )}
                        <p className="text-xs text-slate-500">Only trust devices that are yours and that lock with a PIN, fingerprint or password.</p>
                    </div>
                )}
                {me.config.tailnet.available && !me.config.tailnet.signIn && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                        One-tap sign-in is switched off on this site at the moment. You can trust devices now; the button appears
                        on the login page once the managing broker switches it on.
                    </p>
                )}

                <h3 className="font-semibold text-slate-800 pt-2">My trusted devices</h3>
                {mine.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
                    <ul className="space-y-2">{mine.map((d) => (
                        <Row key={d.device_id}
                            title={<>{d.name} <Pill status={d.status} />{here && d.tailnet_ip === here.peer && <span className="text-xs text-slate-500"> · this device</span>}</>}
                            lines={[`Tailscale address ${d.tailnet_ip}${d.node_os ? ` · ${d.node_os}` : ''}`,
                                `Last used ${fmt(d.last_used_at)}${d.last_used_ip ? ` from ${d.last_used_ip}` : ''}`]}
                            actions={d.status === 'active' || d.status === 'pending' ? <>
                                <button className={link} disabled={busy} onClick={() => renameDevice(d)}>Rename</button>
                                <button className="text-sm text-red-600 hover:underline" disabled={busy} onClick={() => removeDevice(d)}>Remove</button>
                            </> : null} />
                    ))}</ul>
                )}
            </div>

            {/* ---------------- Passkeys ---------------- */}
            <div className={card}>
                <h2 className="text-lg font-semibold text-slate-800">Passkeys</h2>
                <p className="text-sm text-slate-600">
                    A passkey lives on your phone, computer or password manager and is unlocked with your fingerprint, face or
                    device PIN. On the login page, press "Sign in with a passkey". Add one on each phone or computer you use
                    (a passkey saved to your Google or Apple account follows you to your other devices).
                </p>
                {!passkeysOn ? (
                    <p className="text-sm text-slate-500">Passkeys are switched off on this site.</p>
                ) : !passkeysSupported() ? (
                    <p className="text-sm text-amber-800">This browser cannot make passkeys. Try Chrome, Edge or Safari, or the BREC app.</p>
                ) : (
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <input className={input} value={passkeyName} maxLength={80} onChange={(e) => setPasskeyName(e.target.value)}
                            placeholder="Name, e.g. My phone" />
                        <button className={btn} disabled={busy} onClick={addPasskey}>Add a passkey</button>
                    </div>
                )}
                <h3 className="font-semibold text-slate-800 pt-2">My passkeys</h3>
                {(me.passkeys || []).length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
                    <ul className="space-y-2">{me.passkeys.map((c) => (
                        <Row key={c.passkey_id} title={c.name || 'Passkey'}
                            lines={[`Added ${fmt(c.created_at)}${c.backed_up ? ' · synced to your account' : ''}`,
                                `Last used ${fmt(c.last_used_at)}${c.last_used_ip ? ` from ${c.last_used_ip}` : ''}`]}
                            actions={<>
                                <button className={link} disabled={busy} onClick={() => renamePasskey(c)}>Rename</button>
                                <button className="text-sm text-red-600 hover:underline" disabled={busy} onClick={() => removePasskey(c)}>Remove</button>
                            </>} />
                    ))}</ul>
                )}
            </div>

            {me.isManagingBroker && <OfficePanel api={api} onChanged={load} />}
        </div>
    );
}
